/**
 * Pure transforms and fixed palettes for the admin dashboards.
 *
 * The category and series spellings below were read off the July 2026
 * consultant delivery (57,090 rows) so nobody has to open that file again:
 *   party_preference_weighted: DEM, LEAN DEM, OTHER, LEAN OTHER, REP, LEAN REP
 *   voting_preference_weighted: MAIL, LEAN MAIL, EARLY, LEAN EARLY, EDAY,
 *     LEAN EDAY, MIXED, NEVER, NEW
 *   age_range: 18_24, 25_44, 45_64, 65_plus
 * The API reports a null as the literal string Unknown, so Unknown is the last
 * member of every ORDER array.
 *
 * The ORDER arrays are a display order, not a filter. A value that appears in
 * the data but not in its ORDER array is charted anyway: appended after the
 * known values in ascending locale order, before Unknown, with a deterministic
 * fallback colour. A new preference value in a future delivery therefore shows
 * up instead of silently vanishing.
 */

export const UNKNOWN = 'Unknown';

export interface PivotResult {
  categories: string[];
  series: { label: string; values: number[] }[];
}

export interface OrderedPivot {
  categories: string[];
  series: { label: string; values: number[]; color: string }[];
}

// Cell keys join a category and a series label with a NUL, which can never
// appear in either, so the join is unambiguous.
const SEP = '\u0000';

/** Long rows to categories (rowKey) by series (colKey). Raw keys, first-seen order. */
export function pivot(
  rows: Record<string, unknown>[],
  rowKey: string,
  colKey: string,
  valueKey: string,
): PivotResult {
  const categories: string[] = [];
  const labels: string[] = [];
  const cells = new Map<string, number>();
  for (const r of rows) {
    const cat = String(r[rowKey]);
    const lab = String(r[colKey]);
    const val = Number(r[valueKey]) || 0;
    if (!categories.includes(cat)) categories.push(cat);
    if (!labels.includes(lab)) labels.push(lab);
    const k = cat + SEP + lab;
    cells.set(k, (cells.get(k) ?? 0) + val);
  }
  return {
    categories,
    series: labels.map(lab => ({
      label: lab,
      values: categories.map(cat => cells.get(cat + SEP + lab) ?? 0),
    })),
  };
}

/**
 * The /activity payload is a nested map per week, which pivot cannot consume.
 * Categories stay on the full week list so weeks with no contacts keep their
 * slot on the axis.
 */
export function pivotWeeks(
  weeks: { week_start: string; results: Record<string, number> }[],
): PivotResult {
  const rows: Record<string, unknown>[] = [];
  for (const w of weeks) {
    for (const [result, count] of Object.entries(w.results)) {
      rows.push({ week: w.week_start, result, count });
    }
  }
  const p = pivot(rows, 'week', 'result', 'count');
  const categories = weeks.map(w => w.week_start);
  return {
    categories,
    series: p.series.map(s => ({
      label: s.label,
      values: categories.map(c => {
        const i = p.categories.indexOf(c);
        return i === -1 ? 0 : s.values[i];
      }),
    })),
  };
}

/** Known values first in their given order, then the rest alphabetically, Unknown last. */
export function orderKeys(present: string[], known: string[]): string[] {
  const seen = new Set(present);
  const knownSet = new Set(known);
  const head = known.filter(k => k !== UNKNOWN && seen.has(k));
  const tail = [...seen]
    .filter(k => k !== UNKNOWN && !knownSet.has(k))
    .sort((a, b) => a.localeCompare(b));
  return seen.has(UNKNOWN) ? [...head, ...tail, UNKNOWN] : [...head, ...tail];
}

const PROPENSITY_BUCKET_ORDER = [
  '0',
  '0.01 to 0.20',
  '0.21 to 0.40',
  '0.41 to 0.60',
  '0.61 to 0.80',
  '0.81 to 1.00',
  UNKNOWN,
];

/** Bucket keys in display order: "0" first, ranges ascending, Unknown last. */
export function orderBuckets(buckets: string[]): string[] {
  return orderKeys(buckets, PROPENSITY_BUCKET_ORDER);
}

/** Display label for a bucket key. Applied at the axis, never as a lookup key. */
export function labelBucket(b: string): string {
  return b === '0' ? 'No history' : b;
}

export const PARTY_PREFERENCE_ORDER = [
  'DEM',
  'LEAN DEM',
  'OTHER',
  'LEAN OTHER',
  'LEAN REP',
  'REP',
  UNKNOWN,
];

export const VOTING_PREFERENCE_ORDER = [
  'MAIL',
  'LEAN MAIL',
  'EARLY',
  'LEAN EARLY',
  'EDAY',
  'LEAN EDAY',
  'MIXED',
  'NEVER',
  'NEW',
  UNKNOWN,
];

export const AGE_RANGE_ORDER = ['18_24', '25_44', '45_64', '65_plus', UNKNOWN];

export const PARTY_PREFERENCE_LABELS: Record<string, string> = {
  DEM: 'DEM',
  'LEAN DEM': 'Lean DEM',
  OTHER: 'OTHER',
  'LEAN OTHER': 'Lean OTHER',
  'LEAN REP': 'Lean REP',
  REP: 'REP',
  Unknown: 'Unknown',
};

export const VOTING_PREFERENCE_LABELS: Record<string, string> = {
  MAIL: 'Mail',
  'LEAN MAIL': 'Lean mail',
  EARLY: 'Early',
  'LEAN EARLY': 'Lean early',
  EDAY: 'Election day',
  'LEAN EDAY': 'Lean election day',
  MIXED: 'Mixed',
  NEVER: 'Never voted',
  NEW: 'New registrant',
  Unknown: 'Unknown',
};

export const AGE_RANGE_LABELS: Record<string, string> = {
  '18_24': '18 to 24',
  '25_44': '25 to 44',
  '45_64': '45 to 64',
  '65_plus': '65 plus',
  Unknown: 'Unknown',
};

export function labelPartyPreference(p: string): string {
  return PARTY_PREFERENCE_LABELS[p] ?? p;
}

export function labelVotingPreference(v: string): string {
  return VOTING_PREFERENCE_LABELS[v] ?? v;
}

export function labelAgeRange(a: string): string {
  return AGE_RANGE_LABELS[a] ?? a;
}

// Party: blue for DEM, red for REP, grey for OTHER, lighter tints for the LEAN
// variants, muted teal for Unknown (kept apart from LEAN OTHER's cool grey so
// the two do not sit adjacent in the same stacked bar as near-duplicate light
// greys, and apart from FALLBACK_COLORS so an unmapped party value never
// lands on a colour Unknown already owns; a warm tan was tried first but sat
// only ~13 delta-E from FALLBACK_COLORS[5]).
export const PARTY_PREFERENCE_COLORS: Record<string, string> = {
  DEM: '#1f5c99',
  'LEAN DEM': '#7fa9d4',
  OTHER: '#6b7280',
  'LEAN OTHER': '#b0b6c0',
  'LEAN REP': '#d98a8e',
  REP: '#a5262c',
  Unknown: '#568f73',
};

// Voting preference: three hues for mail, early and election day, a lighter
// tint of each for the LEAN variants, then mixed, never and new.
export const VOTING_PREFERENCE_COLORS: Record<string, string> = {
  MAIL: '#6b4c9a',
  'LEAN MAIL': '#a892c9',
  EARLY: '#1f7a73',
  'LEAN EARLY': '#85bdb8',
  EDAY: '#b5761f',
  'LEAN EDAY': '#dcb27a',
  MIXED: '#8a8f98',
  NEVER: '#3f4650',
  NEW: '#2d7a4f',
  Unknown: '#d9d5cb',
};

// The ten contact_result values, in the order of the DB CHECK constraint in
// backend/app/models/contact.py. Labels copied from VoterHistory.svelte, the
// one existing map that covers all ten.
export const CONTACT_RESULT_ORDER = [
  'spoke',
  'not_home',
  'refused',
  'moved',
  'wrong_address',
  'no_response',
  'wrong_number',
  'sent',
  'returned_undeliverable',
  'signed_petition',
];

export const CONTACT_RESULT_LABELS: Record<string, string> = {
  spoke: 'Spoke',
  not_home: 'Not home',
  refused: 'Refused',
  moved: 'Moved',
  wrong_address: 'Wrong address',
  no_response: 'No response',
  wrong_number: 'Wrong number',
  sent: 'Sent',
  returned_undeliverable: 'Returned undeliverable',
  signed_petition: 'Signed petition',
};

export const CONTACT_RESULT_COLORS: Record<string, string> = {
  spoke: '#1f5135',
  not_home: '#c2a878',
  refused: '#9b3340',
  moved: '#6b4c9a',
  wrong_address: '#8a5a2b',
  no_response: '#9ca3af',
  wrong_number: '#5b6670',
  sent: '#2d5c8a',
  returned_undeliverable: '#c2185b',
  signed_petition: '#c99326',
};

// Neutral ramp for values that are not in any palette yet. Deterministic per
// key, so the same new value keeps the same colour across reloads. Exported
// so tests can check new palette entries stay distinct from it (see the
// 'fixed vocabularies' distinctness test in charts.test.ts).
export const FALLBACK_COLORS = ['#7a7872', '#8c6d1f', '#7a3d5c', '#946b4e', '#4a524c', '#a09a8a'];

export function fallbackColor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) % 100000;
  return FALLBACK_COLORS[h % FALLBACK_COLORS.length];
}

/** Build a colour lookup that never returns undefined. */
export function colorFrom(map: Record<string, string>): (key: string) => string {
  return (key: string) => map[key] ?? fallbackColor(key);
}

/**
 * Reorder a pivot, label its axes and attach the colours StackedBars needs.
 * Keys the caller's ordering leaves out are appended rather than dropped, so a
 * partial order can never lose data.
 */
export function orderPivot(
  p: PivotResult,
  opts: {
    categoryOrder?: (keys: string[]) => string[];
    categoryLabel?: (key: string) => string;
    seriesOrder?: string[];
    seriesLabel?: (key: string) => string;
    colorOf: (key: string) => string;
  },
): OrderedPivot {
  const wanted = opts.categoryOrder ? opts.categoryOrder(p.categories) : p.categories;
  const catKeys = wanted.filter(k => p.categories.includes(k));
  for (const k of p.categories) if (!catKeys.includes(k)) catKeys.push(k);
  const idx = catKeys.map(k => p.categories.indexOf(k));

  const byLabel = new Map(p.series.map(s => [s.label, s]));
  const seriesKeys = orderKeys([...byLabel.keys()], opts.seriesOrder ?? []);
  const seriesLabel = opts.seriesLabel ?? ((k: string) => k);
  const categoryLabel = opts.categoryLabel ?? ((k: string) => k);

  return {
    categories: catKeys.map(categoryLabel),
    series: seriesKeys.map(k => {
      // seriesKeys comes from orderKeys([...byLabel.keys()], ...), which only
      // ever returns members of the `present` array it was given, so every key
      // here is a key of byLabel: this lookup cannot miss.
      const s = byLabel.get(k) as { label: string; values: number[] };
      return {
        label: seriesLabel(k),
        values: idx.map(i => s.values[i]),
        color: opts.colorOf(k),
      };
    }),
  };
}

/** Precinct keys sorted by opted-in share descending, ties alphabetically. */
export function sortPrecinctsByOptedInShare(
  rows: { precinct: string; state: string; count: number }[],
): string[] {
  const total = new Map<string, number>();
  const opted = new Map<string, number>();
  for (const r of rows) {
    total.set(r.precinct, (total.get(r.precinct) ?? 0) + r.count);
    if (r.state === 'opted_in') {
      opted.set(r.precinct, (opted.get(r.precinct) ?? 0) + r.count);
    }
  }
  return [...total.keys()].sort((a, b) => {
    const shareA = (opted.get(a) ?? 0) / (total.get(a) || 1);
    const shareB = (opted.get(b) ?? 0) / (total.get(b) || 1);
    if (shareB !== shareA) return shareB - shareA;
    return a.localeCompare(b);
  });
}

/** "2026-09-07" to "9/7", short enough for 26 axis labels. */
export function weekLabel(isoDate: string): string {
  const parts = isoDate.split('-');
  return `${Number(parts[1])}/${Number(parts[2])}`;
}

/**
 * A share as a percentage, one decimal, 0 when the denominator is 0. The
 * scorecard's two computed columns use this: text contacted over registered,
 * and spoke over door contacts.
 */
export function percentOf(numerator: number, denominator: number): number {
  if (!denominator) return 0;
  return Math.round((numerator / denominator) * 1000) / 10;
}
